import { useEffect, useState } from "react";
import { Download, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, downloadCsv, errMsg } from "../lib/api";
import { Section, Tile, Toggle, StatusPill, KpiChips, kpiLabelMap } from "./Common";
import { fmtNum, pct, fmtShort, dayShort, hmWIB, addDays } from "../lib/fmt";
import { canEdit } from "./TeamReports";
import Comments from "./Comments";

export default function MyReports({ data, onEdit }) {
  const [days, setDays] = useState(30);
  const [mine, setMine] = useState(null);
  const labels = kpiLabelMap(data.jobFunctions);
  const stamp = data.myToday ? `${data.myToday.code}-${data.myToday.editedAt}` : "none";
  useEffect(() => {
    api.get("/reports/mine", { params: { days } }).then((r) => setMine(r.data)).catch(() => {});
  }, [days, stamp]);
  const download = async () => {
    try {
      await downloadCsv({ from: addDays(data.today, -(days - 1)), to: data.today, memberId: data.me.id }, `laporan-saya_${days}hari.csv`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const s = mine?.score;
  return (
    <Section id="mine" eyebrow="Laporan Saya" title="Riwayat & performa pribadi"
      right={<Toggle testid="mine-days-toggle" value={days} onChange={setDays} options={[[30, "30 hari"], [90, "90 hari"]]} />}>
      {s && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="mine-stats">
          <Tile testid="mine-filled" accent label="Hari terisi" value={`${s.filled}/${s.due}`} />
          <Tile testid="mine-pct" label="Kedisiplinan" value={pct(s.pct)} />
          <Tile testid="mine-ontime" label="Tepat waktu" value={pct(s.onTimePct)} />
          <Tile testid="mine-streak" label="Streak" value={`${s.streak} hari`} />
          <Tile testid="mine-issues" label="Delay/Blocked" value={s.delayBlocked} />
          <Tile testid="mine-leave" label="Hari cuti" value={s.leaveDays} />
        </div>
      )}
      {mine?.kpiTotals?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="mine-kpi-totals">
          {mine.kpiTotals.map((k) => (
            <span key={k.key} className="rounded-xl border border-brand-line px-3 py-2 text-sm">{k.label}: <b className="text-brand">{fmtNum(k.total)}</b></span>
          ))}
        </div>
      )}
      <div className="mt-4 flex justify-end">
        <button data-testid="mine-download-csv" onClick={download} className="btn-ghost"><Download size={15} /> Unduh CSV</button>
      </div>
      <div className="mt-3 divide-y divide-brand-line rounded-2xl border border-brand-line" data-testid="mine-list">
        {mine && mine.reports.length === 0 && <p className="p-4 text-sm text-[#7a6667]">Belum ada laporan pada periode ini.</p>}
        {mine?.reports.map((r) => (
          <div key={r.id} className="p-3.5 text-sm">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="w-24 shrink-0 font-bold">{dayShort(r.date)}, {fmtShort(r.date)}</div>
            <div className="w-20 shrink-0 font-extrabold text-brand">{r.code}</div>
            <StatusPill status={r.status} />
            <div className="min-w-0 flex-1 truncate text-[#5c4a4b]">{r.activity.split("\n")[0]}</div>
            <KpiChips kpi={r.kpi} labels={labels} />
            <div className={`text-xs tabular-nums ${r.onTime ? "text-emerald-700" : "text-amber-700"}`}>{hmWIB(r.submittedAt)}{r.editedAt ? " · diedit" : ""}</div>
            {canEdit(data, r) && (
              <button data-testid={`mine-edit-${r.id}`} onClick={() => onEdit(r)} className="btn-ghost min-h-[40px] px-3 text-xs"><Pencil size={13} /> Edit</button>
            )}
          </div>
          {r.program && <span className="mt-1 inline-block rounded-full bg-brand-tint px-2 py-0.5 text-[11px] font-bold text-brand-deep">{r.program}</span>}
          <Comments report={r} comments={r.comments} canComment={false} meId={data.me.id} onChanged={() => {}} />
          </div>
        ))}
      </div>
    </Section>
  );
}
